import * as THREE from 'three'
import {
  createBody,
  stepBodies,
  type BodyState,
  type BodyTuning,
  type BodyWorld,
  type StepReport,
} from '../depth/body'
import type { SurfaceProbeInput } from '../depth/surface-probe'
import { MAX_BODIES, NO_BIAS, type SlopeBias } from './config'

/**
 * The boxes: spawning, meshes, and the bridge between the solver's state and
 * three.js.
 *
 * `BodyState` is mutated by the solver in place — see the header of
 * `src/depth/body.ts` for why — so this class owns those objects and nothing
 * outside reads them except through `snapshot()`.
 */

const COLOURS: readonly number[] = [0x4fd1c5, 0xf2b134, 0xef6461, 0x6f8cff, 0x9ae66e, 0xd18cf0]

const UP = new THREE.Vector3(0, 1, 0)
const scratchNormal = new THREE.Vector3()

export interface BodiesSnapshot {
  readonly count: number
  /** Share of simulated frames where at least one box had no ground at all. */
  readonly lostShare: number
  readonly resting: number
  readonly sliding: number
  readonly blocked: number
  readonly unsupported: number
}

export class Bodies {
  private readonly geometry = new THREE.BoxGeometry(1, 1, 1)
  private readonly materials: THREE.MeshStandardMaterial[]
  private readonly meshes: THREE.Mesh[] = []
  private readonly scene: THREE.Scene

  private world: BodyWorld = { bodies: [], accumulator: 0 }
  private ring = new Float32Array(16)

  private frames = 0
  private lostFrames = 0
  private counts = { rest: 0, sliding: 0, blocked: 0, unsupported: 0 }

  /** Slope of the last support fit, which is what `Calibrate` zeroes against. */
  private lastSlope: SlopeBias = NO_BIAS
  bias: SlopeBias = NO_BIAS

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.materials = COLOURS.map(
      (color) => new THREE.MeshStandardMaterial({ color, roughness: 0.5, metalness: 0.05 })
    )
  }

  get count(): number {
    return this.world.bodies.length
  }

  get full(): boolean {
    return this.world.bodies.length >= MAX_BODIES
  }

  /** XZ of the most recently dropped box, or null when there are none. */
  get anchor(): BodyState | null {
    return this.world.bodies[this.world.bodies.length - 1] ?? null
  }

  drop(x: number, y: number, z: number, size: number): void {
    if (this.full) return

    const body = createBody(x, y, z, size / 2)
    // A drop landing exactly on a resting box would start fully overlapped, and
    // the min-axis resolve has no defined direction there.
    this.separate(body)

    const material = this.materials[this.meshes.length % this.materials.length]
    if (!material) return

    const mesh = new THREE.Mesh(this.geometry, material)
    mesh.scale.setScalar(size)
    mesh.frustumCulled = false
    this.scene.add(mesh)

    this.world.bodies.push(body)
    this.meshes.push(mesh)
  }

  clear(): void {
    for (const mesh of this.meshes) mesh.removeFromParent()
    this.meshes.length = 0
    this.world = { bodies: [], accumulator: 0 }
    this.counts = { rest: 0, sliding: 0, blocked: 0, unsupported: 0 }
    this.frames = 0
    this.lostFrames = 0
  }

  reset(): void {
    this.clear()
    this.bias = NO_BIAS
    this.lastSlope = NO_BIAS
  }

  /** Zeroes the gravity drive against the slope currently under the boxes. */
  calibrate(): void {
    this.bias = this.lastSlope
  }

  /** Runs the solver against one frame's geometry. Returns its report. */
  simulate(
    probe: SurfaceProbeInput,
    tuning: BodyTuning,
    deltaSeconds: number
  ): StepReport | null {
    if (this.world.bodies.length === 0 || deltaSeconds <= 0) return null

    if (this.ring.length !== tuning.probeDirections) {
      this.ring = new Float32Array(tuning.probeDirections)
    }

    const report = stepBodies(this.world, probe, tuning, deltaSeconds, this.ring)

    this.frames += 1
    if (report.unsupported > 0) this.lostFrames += 1
    this.counts = {
      rest: report.resting,
      sliding: report.sliding,
      blocked: report.blocked,
      unsupported: report.unsupported,
    }
    if (report.focus) {
      this.lastSlope = { x: report.focus.slopeX, z: report.focus.slopeZ }
    }

    return report
  }

  /** Copies solver state onto the meshes. Cheap enough to run every frame. */
  syncMeshes(): void {
    for (let i = 0; i < this.world.bodies.length; i += 1) {
      const body = this.world.bodies[i]
      const mesh = this.meshes[i]
      if (!body || !mesh) continue

      mesh.position.set(body.x, body.y, body.z)
      // Yaw is never integrated, which is what keeps box-vs-box collision an
      // exact AABB problem; only the tilt onto the sensed surface is applied.
      scratchNormal.set(body.normalX, 1, body.normalZ).normalize()
      mesh.quaternion.setFromUnitVectors(UP, scratchNormal)
    }
  }

  snapshot(): BodiesSnapshot {
    return {
      count: this.world.bodies.length,
      lostShare: this.frames > 0 ? this.lostFrames / this.frames : 0,
      resting: this.counts.rest,
      sliding: this.counts.sliding,
      blocked: this.counts.blocked,
      unsupported: this.counts.unsupported,
    }
  }

  dispose(): void {
    this.clear()
    this.geometry.dispose()
    for (const material of this.materials) material.dispose()
  }

  private separate(body: BodyState): void {
    for (const other of this.world.bodies) {
      const reach = body.half + other.half
      const dx = body.x - other.x
      const dz = body.z - other.z
      if (Math.abs(dx) >= reach || Math.abs(dz) >= reach) continue

      const distance = Math.hypot(dx, dz)
      if (distance > 1e-4) {
        const push = (reach - distance) / distance
        body.x += dx * push
        body.z += dz * push
      } else {
        body.x += reach
      }
    }
  }
}
